import * as React from "react";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useTranslation } from "react-i18next";
import { serviceType, vehicleType } from "../constants";

import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../hooks/redux-hooks";
import { getOrderPrice } from "../slices/authSlice";
import * as consts from "../constants";
import {
  showNotification,
  NotificationType,
} from "../slices/notificationSlice";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

// 配置 dayjs 使用时区
dayjs.extend(utc);
dayjs.extend(timezone);

export default function Review(props: any) {
  const orderInfo = props.orderInfo;
  const { t } = useTranslation();
  // console.log('orderInfo in review:', orderInfo);
  const dispatch = useAppDispatch();
  const orderPrice = useAppSelector((state) => state.auth.orderPrice);
  const basicUserInfo = useAppSelector((state) => state.auth.basicUserInfo);

  useEffect(() => {
    if (orderInfo) {
      // 前端展示用，不要往后端传
      delete orderInfo.price_detail;
      dispatch(getOrderPrice(orderInfo) as any)
        .unwrap()
        .then((res: any) => {
          if (res) {
            orderInfo.base_fare = res.totalFee;
            //orderInfo.tips = Math.round(res.totalFee * 0.1 * 100) / 100;
          }
        })
        .catch((error: any) => {
          // console.error('获取价格信息失败:', error);
          // 可以在这里添加错误处理逻辑，比如显示错误提示
          dispatch(
            showNotification({
              message: t("errGetPriceFailed"),
              type: NotificationType.Error,
            })
          );
        });
    }
  }, []);

  const contactMethod = orderInfo?.contact_method;
  const contactMethodLabel =
    contactMethod === consts.contactMethod.WECHAT
      ? t("contactMethodWechatOption")
      : contactMethod === consts.contactMethod.LINE
      ? t("contactMethodLineOption")
      : t("contactMethodPhoneOption");

  const contactDetailLabel =
    contactMethod === consts.contactMethod.WECHAT
      ? t("wechatId")
      : contactMethod === consts.contactMethod.LINE
      ? t("lineId")
      : t("phone");

  const contactDetailValue =
    contactMethod === consts.contactMethod.WECHAT
      ? orderInfo?.wechat_id
      : contactMethod === consts.contactMethod.LINE
      ? orderInfo?.line_id
      : orderInfo?.phone;

  const renderPriceDetails = () => {
    if (!orderPrice) {
      return (
        <Typography variant="body1" color="error" sx={{ mb: 2 }}>
          {t("errNoPriceInfo")}
        </Typography>
      );
    }

    // if (!orderPrice) return null;

    // 只显示非零值的价格项
    const priceItems = [
      {
        name: t("labelBaseFee"),
        detail: `$${orderPrice.baseFee}`,
        value: orderPrice.baseFee,
      },
      // { name: t("labelPickupFee"), detail: `$${orderPrice.pickupFee}`, value: orderPrice.pickupFee },
      {
        name: t("labelAirportServiceFee"),
        detail: `$${orderPrice.serviceFee}`,
        value: orderPrice.serviceFee,
      },
      {
        name: t("labelParkingFee"),
        detail: `$${orderPrice.parkingFee}`,
        value: orderPrice.parkingFee,
      },
      {
        name: t("labelPassengerFee"),
        detail: `$${orderPrice.passengerFee}`,
        value: orderPrice.passengerFee,
      },
      {
        name: t("labelSpecialTimeFee"),
        detail: `$${orderPrice.specialTimeFee}`,
        value: orderPrice.specialTimeFee,
      },
      {
        name: t("labelStopsFee"),
        detail: `$${orderPrice.stopsFee}`,
        value: orderPrice.stopsFee,
      },
      {
        name: t("labelOtherFee"),
        detail: `$${orderPrice.otherFee}`,
        value: orderPrice.otherFee,
      },
      {
        name: t("labelPromotion"),
        detail: `-$${orderPrice.promotion}`,
        value: orderPrice.promotion,
      },
      {
        name: t("labelTotalFee"),
        detail: `$${orderPrice.totalFee}`,
        value: orderPrice.totalFee,
      },
    ].filter((item) => item.value !== 0);
    return (
      <Grid container>
        {priceItems.map((item) => (
          <React.Fragment key={item.name}>
            {/* <Stack
              direction="row"
              spacing={1}
              useFlexGap
              sx={{ width: "100%", mb: 1 }}
            >
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                {item.name}
              </Typography>
              <Typography variant="body2">{item.detail}</Typography>
            </Stack> */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ width: "100%", mb: 1 }}
            >
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                {item.name}
              </Typography>
              <Typography variant="body2">{item.detail}</Typography>
            </Stack>
          </React.Fragment>
        ))}
      {orderInfo.lobby_service && (
        <>
          <Box sx={{ height: '16px' }} />
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ width: "100%", mb: 1 }}
          >
            <Typography variant="body1" sx={{ color: "error.main" }}>
              {t("labelPotentialFee")}
            </Typography>
            <Typography variant="body2" sx={{ color: "error.main" }}>
              {t("labelPotentialFeeNotice")}
            </Typography>
          </Stack>
        </>
      )}
      </Grid>
    );
  };

  const renderAddressSection = () => (
    <>
      <ListItem sx={{ py: 1, px: 0 }}>
        <ListItemText
          primary={
            orderInfo.service_type === serviceType.AIRPORT_PICK_UP
              ? t("dropOffLocation")
              : orderInfo.service_type === serviceType.AIRPORT_DROP_OFF
              ? t("pickupLocation")
              : t("unknown")
          }
        />
        <Typography
          variant="body2"
          sx={{
            maxWidth: "500px",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {orderInfo.addr1}
        </Typography>
      </ListItem>
      {orderInfo.addr2 && (
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText
            primary={
              orderInfo.service_type === serviceType.AIRPORT_PICK_UP
                ? t("dropOffLocation") + "2"
                : orderInfo.service_type === serviceType.AIRPORT_DROP_OFF
                ? t("pickupLocation") + "2"
                : t("unknown")
            }
          />
          <Typography
            variant="body2"
            sx={{
              maxWidth: "500px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {orderInfo.addr2}
          </Typography>
        </ListItem>
      )}
      {orderInfo.addr3 && (
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText
            primary={
              orderInfo.service_type === serviceType.AIRPORT_PICK_UP
                ? t("dropOffLocation") + "3"
                : orderInfo.service_type === serviceType.AIRPORT_DROP_OFF
                ? t("pickupLocation") + "3"
                : t("unknown")
            }
          />
          <Typography
            variant="body2"
            sx={{
              maxWidth: "500px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {orderInfo.addr3}
          </Typography>
        </ListItem>
      )}
      {orderInfo.service_type === serviceType.AIRPORT_DROP_OFF && (
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText primary={t("pickupTime")} />
          <Typography variant="body2">{orderInfo.use_time}</Typography>
          {/* <Typography variant="body2">
            {dayjs(orderInfo.use_time).tz('America/Los_Angeles').format('YYYY-MM-DD HH:mm')}
          </Typography> */}
        </ListItem>
      )}
    </>
  );

  const renderAirportSection = () => (
    <>
      <ListItem sx={{ py: 1, px: 0 }}>
        <ListItemText
          primary={
            orderInfo.service_type === serviceType.AIRPORT_PICK_UP
              ? t("pickupAirport")
              : orderInfo.service_type === serviceType.AIRPORT_DROP_OFF
              ? t("dropOffAirport")
              : t("unknown")
          }
        />
        <Typography variant="body2">{orderInfo.airport}</Typography>
      </ListItem>
      <ListItem sx={{ py: 1, px: 0 }}>
        <ListItemText
          primary={
            orderInfo.service_type === serviceType.AIRPORT_PICK_UP
              ? t("flightArrivalTime")
              : orderInfo.service_type === serviceType.AIRPORT_DROP_OFF
              ? t("flightDepartureTime")
              : t("time")
          }
        />
        <Typography variant="body2">{orderInfo.flight_time}</Typography>
        {/* <Typography variant="body2">{dayjs(orderInfo.flight_time).utc().format('YYYY-MM-DD HH:mm')} == 
          {dayjs(orderInfo.flight_time).tz('America/Los_Angeles').format('YYYY-MM-DD HH:mm')}
        </Typography> */}
      </ListItem>
      {orderInfo.flight_number && (
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText primary={t("airlineIncAndNumber")} />
          <Typography variant="body2">{orderInfo.flight_number}</Typography>
        </ListItem>
      )}
      {orderInfo.lobby_service && (
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText 
            primary={
              orderInfo.service_type === serviceType.AIRPORT_PICK_UP
                ? t("lobbyServicePickup")
                : t("lobbyServiceDropoff")
            } 
          />
          <Typography variant="body2">{t("yes")}</Typography>
        </ListItem>
      )}
    </>
  );

  return (
    <Stack spacing={2}>
      <List disablePadding>
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText primary={t("serviceType")} />
          <Typography variant="body2">
            {orderInfo.service_type === serviceType.AIRPORT_PICK_UP
              ? t("pickupService")
              : orderInfo.service_type === serviceType.AIRPORT_DROP_OFF
              ? t("dropoffService")
              : t("unknown")}
          </Typography>
        </ListItem>
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText primary={t("name")} />
          <Typography variant="body2">{orderInfo.customer_name}</Typography>
        </ListItem>
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText primary={t("contactMethod")} />
          <Typography variant="body2">{contactMethodLabel}</Typography>
        </ListItem>
        {(contactMethod === consts.contactMethod.WECHAT ||
          contactMethod === consts.contactMethod.LINE) && (
          <ListItem sx={{ py: 1, px: 0 }}>
            <ListItemText primary={contactDetailLabel} />
            <Typography variant="body2">{contactDetailValue || "--"}</Typography>
          </ListItem>
        )}
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText primary={t("phone")} />
          <Typography variant="body2">{orderInfo.phone || "--"}</Typography>
        </ListItem>
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText primary={t("emailAddress")} />
          <Typography variant="body2">{orderInfo.email}</Typography>
        </ListItem>
        <Divider sx={{ bgcolor: "secondary.light" }} />
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText primary={t("labelVehicle")} />
          <Typography variant="body2">
            {orderInfo.vehicle_type === vehicleType.MINIVAN
              ? t("btnMiniVan")
              : orderInfo.vehicle_type === vehicleType.LUXURYSUV
              ? t("btnLuxurySUV")
              : t("unknown")}
          </Typography>
        </ListItem>
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText primary={t("passengers")} />
          <Typography variant="body2">{orderInfo.passengers}</Typography>
        </ListItem>
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText primary={t("luggages")} />
          <Typography variant="body2">{orderInfo.luggages}</Typography>
        </ListItem>
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText primary={t("carryOns")} />
          <Typography variant="body2">{orderInfo.carry_on}</Typography>
        </ListItem>
        {orderInfo.other && (
          <ListItem sx={{ py: 1, px: 0 }}>
            <ListItemText primary={t("others")} />
            <Typography variant="body2">{orderInfo.other}</Typography>
          </ListItem>
        )}
        <Divider sx={{ bgcolor: "secondary.light" }} />
        {/* <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText
            primary={
              orderInfo.service_type === serviceType.AIRPORT_PICK_UP
                ? t("dropOffLocation")
                : orderInfo.service_type === serviceType.AIRPORT_DROP_OFF
                ? t("pickupLocation")
                : t("unknown")
            }
          />
          <Typography
            variant="body2"
            sx={{
              maxWidth: "500px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {orderInfo.addr1}
          </Typography>
        </ListItem>
        {orderInfo.addr2 && (
          <ListItem sx={{ py: 1, px: 0 }}>
            <ListItemText
              primary={
                orderInfo.service_type === serviceType.AIRPORT_PICK_UP
                  ? t("dropOffLocation") + "2"
                  : orderInfo.service_type === serviceType.AIRPORT_DROP_OFF
                  ? t("pickupLocation") + "2"
                  : t("unknown")
              }
            />
            <Typography
              variant="body2"
              sx={{
                maxWidth: "500px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {orderInfo.addr2}
            </Typography>
          </ListItem>
        )}
        {orderInfo.addr3 && (
          <ListItem sx={{ py: 1, px: 0 }}>
            <ListItemText
              primary={
                orderInfo.service_type === serviceType.AIRPORT_PICK_UP
                  ? t("dropOffLocation") + "3"
                  : orderInfo.service_type === serviceType.AIRPORT_DROP_OFF
                  ? t("pickupLocation") + "3"
                  : t("unknown")
              }
            />
            <Typography
              variant="body2"
              sx={{
                maxWidth: "500px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {orderInfo.addr3}
            </Typography>
          </ListItem>
        )}
        {orderInfo.service_type === serviceType.AIRPORT_DROP_OFF && (
          <ListItem sx={{ py: 1, px: 0 }}>
            <ListItemText primary={t("pickupTime")} />
            <Typography variant="body2">{orderInfo.use_time}</Typography>
          </ListItem>
        )}
        <Divider sx={{ bgcolor: "secondary.light" }} />
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText
            primary={
              orderInfo.service_type === serviceType.AIRPORT_PICK_UP
                ? t("pickupAirport")
                : orderInfo.service_type === serviceType.AIRPORT_DROP_OFF
                ? t("dropOffAirport")
                : t("unknown")
            }
          />
          <Typography variant="body2">{orderInfo.airport}</Typography>
        </ListItem>
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText
            primary={
              orderInfo.service_type === serviceType.AIRPORT_PICK_UP
                ? t("flightArrivalTime")
                : orderInfo.service_type === serviceType.AIRPORT_DROP_OFF
                ? t("flightDepartureTime")
                : t("time")
            }
          />
          <Typography variant="body2">{orderInfo.flight_time}</Typography>
        </ListItem>
        {orderInfo.flight_number && (
          <ListItem sx={{ py: 1, px: 0 }}>
            <ListItemText primary={t("airlineIncAndNumber")} />
            <Typography variant="body2">{orderInfo.flight_number}</Typography>
          </ListItem>
        )} */}
        {orderInfo.service_type === serviceType.AIRPORT_DROP_OFF ? (
          <>
            {renderAddressSection()}
            <Divider sx={{ bgcolor: "secondary.light" }} />
            {renderAirportSection()}
          </>
        ) : (
          <>
            {renderAirportSection()}
            <Divider sx={{ bgcolor: "secondary.light" }} />
            {renderAddressSection()}
          </>
        )}
        <Divider sx={{ bgcolor: "secondary.light" }} />
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText primary={t("preferredServiceLanguage")} />
          <Typography variant="body2">{orderInfo.language}</Typography>
        </ListItem>
        <Divider sx={{ bgcolor: "secondary.light" }} />
        <ListItem sx={{ py: 1, px: 0 }}>
          <ListItemText primary={t("note")} />
          <Typography
            sx={{ py: 1, px: 0 }}
            variant="body2"
            align="right"
            style={{ wordWrap: "break-word" }}
          >
            {orderInfo.notes}
          </Typography>
        </ListItem>
        {(orderInfo.qrcodeFile || orderInfo.qrcode) && (
          <ListItem sx={{ py: 1, px: 0 }}>
            <ListItemText primary={t("btnQrCode")} />
            <Typography variant="body2" color="success.main">
              {t("msgGateCodeUploaded")}
            </Typography>
          </ListItem>
        )}
      </List>
      <Divider sx={{ bgcolor: "secondary.light" }} />
      <Stack
        direction="column"
        divider={<Divider flexItem />}
        spacing={2}
        sx={{ my: 2 }}
      >
        <div>
          <Typography variant="subtitle2" gutterBottom>
            {t("priceDetails")}
          </Typography>
          {basicUserInfo?.is_guest ? (
            <Typography variant="body1" color="primary" sx={{ mb: 2 }}>
              {t("pleaseLoginToViewPrice")}
            </Typography>
          ) : orderPrice?.baseFee === consts.wrongFare ? (
            <Typography variant="body1" color="error" sx={{ mb: 2 }}>
              {t("pleaseContactCustomerService")}
            </Typography>
          ) : (
            renderPriceDetails()
          )}
        </div>
      </Stack>
    </Stack>
  );
}
